export type WebsiteApplyMode = 'review' | 'auto'
export type WebsitePreviewDevice = 'desktop' | 'mobile'

export interface WebsiteAnchorRect {
  x: number
  y: number
  width: number
  height: number
}

export interface WebsiteViewportState { scrollX: number; scrollY: number }

export interface PendingWebsiteTarget {
  targetId: string
  siteId: string
  revision: number
  label: string
  ordinal: number
}

export interface WebsiteEditContextV2 {
  version: 2
  siteId: string
  expectedRevision: number
  applyMode: WebsiteApplyMode
  targets: Array<{ ordinal: number; targetId: string }>
  pendingChangeSetId?: string
}

export type WebsitePreviewBridgeMessage =
  | { source: 'ros-website-preview'; version: 1; type: 'bridge-ready' | 'select-mode-exited'; frameNonce: string; siteId: string; revision: number }
  | { source: 'ros-website-preview'; version: 1; type: 'viewport-updated'; frameNonce: string; siteId: string; revision: number; viewport: WebsiteViewportState }
  | { source: 'ros-website-preview'; version: 1; type: 'target-selected' | 'target-rect-updated'; frameNonce: string; siteId: string; revision: number; targetId: string; label: string; anchorRect: WebsiteAnchorRect }
  | { source: 'ros-website-preview'; version: 1; type: 'target-deselected'; frameNonce: string; siteId: string; revision: number; targetId: string; label: string }

export type WebsitePreviewBridgeCommand =
  | { source: 'ros-website-host'; version: 1; type: 'set-select-mode'; frameNonce: string; enabled: boolean }
  | { source: 'ros-website-host'; version: 1; type: 'set-selected-targets'; frameNonce: string; targetIds: string[] }
  | { source: 'ros-website-host'; version: 1; type: 'scroll-to-target'; frameNonce: string; targetId: string }
  | { source: 'ros-website-host'; version: 1; type: 'set-highlighted-targets'; frameNonce: string; targetIds: string[] }
  | { source: 'ros-website-host'; version: 1; type: 'set-viewport'; frameNonce: string; viewport: WebsiteViewportState }

export type WebsitePreviewBridgeCommandInput = WebsitePreviewBridgeCommand extends infer Command
  ? Command extends WebsitePreviewBridgeCommand
    ? Omit<Command, 'source' | 'version' | 'frameNonce'>
    : never
  : never

export interface WebsiteChangeSetSummary {
  changeSetId: string
  siteId: string
  baseRevision: number
  title: string
  summary: string
  affectedTargets: Array<{ ordinal: number; targetId: string; label: string }>
  changes: Array<{
    summary: string
    before?: string
    after?: string
    targetId?: string
  }>
  warnings: string[]
  beforeHtml: string
  afterHtml: string
}

export function toWebsiteEditContext(input: {
  siteId: string
  revision: number
  applyMode: WebsiteApplyMode
  targets: PendingWebsiteTarget[]
  pendingChangeSetId?: string
}): WebsiteEditContextV2 {
  return {
    version: 2,
    siteId: input.siteId,
    expectedRevision: input.revision,
    applyMode: input.applyMode,
    targets: input.targets.map(({ ordinal, targetId }) => ({ ordinal, targetId })),
    ...(input.pendingChangeSetId ? { pendingChangeSetId: input.pendingChangeSetId } : {}),
  }
}
